import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { E3Logo } from "@/components/brand/E3Logo";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/usp", label: "Why E3" },
    { to: "/investor", label: "Investors" },
    { to: "/franchise", label: "Franchise" },
    { to: "/technology", label: "Technology" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="e3-navbar"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-stone-50/90 backdrop-blur-xl border-b border-stone-200/70"
                    : "bg-gradient-to-b from-stone-950/55 via-stone-950/30 to-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo-link" className="shrink-0">
                    <E3Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-stone-900 bg-stone-100"
                                        : "text-stone-600 hover:text-stone-900"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <Link
                        to="/contact"
                        data-testid="nav-cta-become-partner"
                        className="e3-btn-primary text-sm"
                    >
                        Become a Partner <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className="lg:hidden p-2 rounded-full hover:bg-stone-100"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-stone-50/95 backdrop-blur-xl border-t border-stone-200">
                    <div className="px-6 py-6 flex flex-col gap-2">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `px-4 py-3 rounded-xl text-base font-medium ${
                                        isActive
                                            ? "bg-stone-900 text-white"
                                            : "text-stone-700"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-cta-become-partner"
                            className="e3-btn-primary justify-center mt-3"
                        >
                            Become a Partner
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
